import casinoConfig from '@dcentralexpedientedigital/config';
import { useIntlStore } from '@dcentralexpedientedigital/fe-shared/stores/intl-store';

import { isValidLocale } from './language';

// Temporary mapping for locales - Some browsers use different formats for the same locale
const LOCALE_MAPPING: Record<string, string> = {
    'no-no': 'nb-no',
    'nn-no': 'nb-no',
    'no': 'nb-no',
    'nb': 'nb-no',
    'nb-sj': 'nb-no',
    'nn-sj': 'nb-no',
    'nn': 'nb-no',
};

const mapLocale = (locale: string): string => {
    const normalizedLocale = locale.toLowerCase();
    return LOCALE_MAPPING[normalizedLocale] ?? normalizedLocale;
};

/**
 * Detect the locale based on the priority order:
 * 1. URL override (handled by useSyncLocaleWithUrl hook)
 * 2. Browser language (on first visit when no preference is saved yet)
 * 3. User preference from localStorage
 * 4. Default locale
 */
export const getLocaleFromStoreOrGeolocation = () => {
    const { locale } = useIntlStore.getState();
    const defaultLocale = casinoConfig.defaultLanguage.toLowerCase();

    const savedLocale = localStorage.getItem('intl-storage');
    let hasSavedPreference = false;

    if (savedLocale) {
        try {
            const parsed = JSON.parse(savedLocale);
            hasSavedPreference = parsed.state?.locale && isValidLocale(parsed.state.locale);
        } catch {
            hasSavedPreference = false;
        }
    }

    if (hasSavedPreference && locale && isValidLocale(locale)) {
        const mappedLocale = mapLocale(locale);
        return mappedLocale.toLowerCase();
    }

    const browserLanguage = (navigator.language || navigator.languages?.[0])?.toLowerCase();
    const mappedBrowserLanguage = browserLanguage ? mapLocale(browserLanguage) : undefined;

    if (mappedBrowserLanguage && isValidLocale(mappedBrowserLanguage) && !hasSavedPreference) {
        useIntlStore.getState().setLocale(mappedBrowserLanguage.toLowerCase());
        return mappedBrowserLanguage;
    }

    return defaultLocale;
};
